/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import { CardMetadata, CardSkills, Grid, ProductCard } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { MiniSDPQuery } from 'bundles/coursera-ui/components/rich/MiniXDP/MiniSDPContainer';
import type {
  MiniSDPQuery as MiniSDPQueryData,
  MiniSDPQueryVariables,
  MiniSDPQuery_XdpV1Resource_get_xdpMetadata_XdpV1_sdpMetadataMember as SDPMetadataType,
} from 'bundles/coursera-ui/components/rich/MiniXDP/__generated__/MiniSDPQuery';
import ProductCardReviews from 'bundles/product-card/components/ProductCardReviews';
import { useEventingData } from 'bundles/unified-career-academy/contexts/EventingContext';

import _t from 'i18n!nls/career-academy';

function getDifficultyLevelString(level: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED') {
  const DIFFICULTY_LEVEL_TO_STRING = {
    BEGINNER: _t('Beginner'),
    INTERMEDIATE: _t('Intermediate'),
    ADVANCED: _t('Advanced'),
  };
  return DIFFICULTY_LEVEL_TO_STRING[level];
}

export type Props = {
  productId: string;
  index: number;
};

export const RolePageConsumerCertificateCard = ({ productId, index }: Props) => {
  const eventingData = useEventingData();
  const track = useTracker();
  const { data } = useQuery<MiniSDPQueryData, MiniSDPQueryVariables>(MiniSDPQuery, {
    variables: { id: `SPECIALIZATION~${productId}` },
  });

  const productCardEventingV3Data = {
    productCard: {
      index,
    },
    product: {
      id: productId,
    },
    ...eventingData,
  };

  const productCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    productCardEventingV3Data
  );

  const SDPMetadata = data?.XdpV1Resource?.get?.xdpMetadata as SDPMetadataType;

  if (!productId || !SDPMetadata) {
    return null;
  }

  const s12nMetadata = SDPMetadata.sdpMetadata;
  const partnerLogo = s12nMetadata.partners[0].squareLogo || '';
  const partnerName = s12nMetadata.partners[0].name || '';
  const certificateImg = s12nMetadata.certificateLogo || '';
  const certificateName = s12nMetadata.name || '';
  const skills = s12nMetadata.s12nAndCourseSkills.slice(0, 5);
  const avgRating = s12nMetadata.ratings?.averageFiveStarRating ?? 0;
  const ratingCountRaw = s12nMetadata.ratings?.ratingCount ?? 0;
  const level = s12nMetadata.level || 'BEGINNER';
  const avgMonthsDuration = s12nMetadata.avgLearningMonthsAdjusted || 6;

  const trackClick = () => {
    track('click_product_card', productCardEventingV3Data);
  };

  return (
    <Grid item xs={6} md={3} key={s12nMetadata.slug} data-testid="consumer-rdp-product-card">
      <ProductCard
        ref={productCardRef}
        variant="grid"
        productType="Professional Certificate"
        partners={[{ name: partnerName, logoUrl: partnerLogo }]}
        previewImageSrc={certificateImg}
        title={{
          name: certificateName,
          linkProps: { href: `/professional-certificates/${s12nMetadata.slug}`, target: '_blank', onClick: trackClick },
        }}
        body={<CardSkills skills={skills} />}
        footer={
          <>
            <ProductCardReviews avgProductRating={avgRating} numProductRatings={ratingCountRaw} />
            <CardMetadata
              metadata={[
                getDifficultyLevelString(level),
                _t('Professional Certificate'),
                `${_t('#{avgMonthsDuration} months', { avgMonthsDuration })}`,
              ]}
            />
          </>
        }
      />
    </Grid>
  );
};
